/**
 * Add one block to a booking in progress and pay for it (Simulator Companion slice 9).
 *
 * Returns the envelope unchanged (internalapi-envelope): a declined card, a stale end
 * and a closed gap are refusals the row renders, not exceptions. Settled, not success,
 * invalidates the booking and its offer: a refused extension can still mean the end
 * moved under the member (someone else extended first), and the row must re-read.
 *
 * @module booking/hooks/useExtendBooking
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ApiExtendBookingRequest, ApiExtendBookingResponse } from '@lgc/database/types/domains/booking';
import { queryKeys } from '~/lib/query/keys';
import type { ApiResult } from '~/lib/apiClient/types';
import { bookingService } from '../api/bookingService';

export interface UseExtendBookingOptions {
  /** The club the booking belongs to; its booking lists are refreshed on settle. */
  clubId: number | null;
}

export function useExtendBooking({ clubId }: UseExtendBookingOptions) {
  const queryClient = useQueryClient();
  return useMutation<ApiResult<ApiExtendBookingResponse>, Error, { bookingId: number; body: ApiExtendBookingRequest }>({
    mutationFn: ({ bookingId, body }) => bookingService.extendBooking(bookingId, body),
    onSettled: (_result, _error, { bookingId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.bookings.detail(bookingId) });
      if (clubId !== null) void queryClient.invalidateQueries({ queryKey: queryKeys.bookings.all(clubId) });
    },
  });
}
